import { LocalStorageService } from '../services/local-storage.service';

type StoredPermission = {
	moduleName: string;
	subModuleName: string;
	action: string;
};

type PermissionAlias = {
	moduleName: string;
	subModuleName?: string;
};

export const PERMISSIONS_ALIAS_MAP: Record<string, PermissionAlias> = {
	documents: { moduleName: 'documentos' },
	resources: { moduleName: 'gestion_y_admin' },
	projects: { moduleName: 'proyectos-pedagogicos' },
	institutions: { moduleName: 'instituciones' },
	users: { moduleName: 'usuarios' },
	roles: { moduleName: 'roles' },
	route: { moduleName: 'plan-mejoramiento' },
	'institutional-resume': { moduleName: 'hvi' },
};

export const SUBMODULE_ALIAS_MAP: Record<string, Record<string, string>> = {
	resources: {
		inventary: 'inventario',
		reserves: 'reservas',
	},
	route: {
		autoevaluation: 'autoevaluacion',
		plan: 'plan_de_mejoramiento',
		tracking: 'seguimiento',
	},
	'institutional-resume': {
		institution_headquarter_management: 'instituciones_y_sedes',
	},
	hvi: {
		institution_headquarter_management: 'instituciones_y_sedes',
	},
};

export const ACTION_ALIAS_MAP: Record<string, string> = {
	view: 'listar',
	create: 'crear',
	update: 'actualizar',
	delete: 'eliminar',
	merge: 'fusionar',
	deliver: 'entregar',
	receive: 'recibir',
	approve: 'aprobar',
	reject: 'rechazar',
	modify_status: 'actualizar',
	form: 'crear',
	send: 'crear',
	create_institution: 'crear',
	update_institution: 'actualizar',
	view_institution: 'listar',
	delete_institution: 'eliminar',
	merge_institution: 'fusionar',
	create_headquarter: 'crear',
	update_headquarter: 'actualizar',
	view_headquarter: 'listar',
	delete_headquarter: 'eliminar',
	merge_headquarter: 'fusionar',
};

/**
 * Resuelve los nombres de módulo, submódulo y acción a sus equivalentes en español según los mapas de alias.
 * @param moduleName - Nombre del módulo.
 * @param submodule - Nombre del submódulo (opcional).
 * @param action - Acción a resolver (opcional).
 * @returns Un objeto con los nombres resueltos de módulo, submódulo y acción.
 */
const resolvePermission = (
	moduleName: string,
	submodule?: string | null,
	action?: string,
) => {
	const moduleAlias = PERMISSIONS_ALIAS_MAP[moduleName];
	const resolvedModuleName = moduleAlias?.moduleName ?? moduleName;
	const submoduleAliases = SUBMODULE_ALIAS_MAP[moduleName];
	const resolvedSubModuleName = moduleAlias?.subModuleName ??
		submoduleAliases?.[submodule ?? ''] ?? submodule ?? '';
	const resolvedAction = action ? ACTION_ALIAS_MAP[action] ?? action : undefined;

	return {
		moduleName: resolvedModuleName,
		subModuleName: resolvedSubModuleName,
		action: resolvedAction,
	};
};

/**
 * Obtiene los permisos almacenados en el localStorage.
 * @returns Un arreglo de permisos almacenados.
 */
const getStoredPermissions = (): StoredPermission[] => {
	try {
		const ls = new LocalStorageService();
		const permissions = JSON.parse(ls.getItem('permissions', '[]'));
		return Array.isArray(permissions) ? permissions as StoredPermission[] : [];
	} catch {
		return [];
	}
};

/**
 * Verifica si el usuario tiene un permiso específico para un módulo, submódulo y acción.
 * @param moduleName - Nombre del módulo.
 * @param submodule - Nombre del submódulo (opcional).
 * @param action - Acción a verificar (opcional).
 * @returns `true` si el usuario tiene el permiso, de lo contrario `false`.
 */
export const hasPermission = (
	moduleName: string,
	submodule?: string | null,
	action?: string,
): boolean => {
	const ls = new LocalStorageService();
	if (ls.isAdminRole) {
		return true;
	}
	const resolvedPermission = resolvePermission(moduleName, submodule, action);
	const isModuleOnlyCheck = submodule == null && !action;

	return getStoredPermissions().some((permission) => {
		if (permission.moduleName !== resolvedPermission.moduleName) return false;
		if (isModuleOnlyCheck) {
			const resolvedAction = ACTION_ALIAS_MAP[permission.action] ?? permission.action;
			return resolvedAction === 'listar' || resolvedAction === 'ver';
		}
		if (permission.subModuleName !== resolvedPermission.subModuleName) return false;
		if (resolvedPermission.action && permission.action !== resolvedPermission.action) return false;

		return true;
	});
};

/**
 * Valida el acceso a un módulo y submódulo específico.
 * También valida que tenga permisos sobre ese módulo y acción.
 * @param moduleName - Nombre del módulo.
 * @param subModule - Nombre del submódulo.
 * @returns `true` si el usuario tiene acceso, de lo contrario `false`.
 */
export const validateAccess = (moduleName: string, subModule?: string): boolean => {
	const ls = new LocalStorageService();

	if (ls.isAdminRole) {
		return true;
	}

	return hasPermission(moduleName, subModule);
};


/**
 * Valida el acceso a una acción específica en un submódulo.
 * @param moduleName - Nombre del módulo.
 * @param submodule - Nombre del submódulo.
 * @param action - Acción a validar.
 * @returns `true` si el usuario tiene acceso, de lo contrario `false`.
 * Legacy: Antes validaba que los subroles de los usuarios tuvieran accesos al modulo, submodulo y acción. Ahora valida que el usuario tenga permisos para el módulo, submodulo y acción.
 */
export const validateSubRole = (moduleName: string, submodule: string | null, action: string): boolean => {
	const ls = new LocalStorageService();

	if (ls.isAdminRole) {
		return true;
	}

	return hasPermission(moduleName, submodule, action);
};
